import { Modal, useModals, ModalProps, Button, Input, Stack } from "@skailer-com/uikit";
import { toCleanNumber } from "@/helpers/utils.helper.ts";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useWorkPatternSave } from "@/queries/use-work-patterns.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiWorkPattern, ApiWorkPatternPayload } from "@/types/work-pattern.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface WorkPatternEditModelProps extends ModalProps {
  workPattern?: ApiWorkPattern;
}

export function WorkPatternEditModal({ workPattern, ...props }: WorkPatternEditModelProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { id } = { ...props };

  const WorkPatternSchemaObject = z.object({
    title: z.string().min(1, t("errors:field_required")),
    monday: z.string().trim(),
    tuesday: z.string().trim(),
    wednesday: z.string().trim(),
    thursday: z.string().trim(),
    friday: z.string().trim(),
    saturday: z.string().trim(),
    sunday: z.string().trim(),
  });

  const getDefaultValue = (value?: string) => {
    if (!value || !parseFloat(value)) return "";
    return toCleanNumber(value);
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(WorkPatternSchemaObject),
    defaultValues: {
      title: workPattern?.title ?? "",
      monday: getDefaultValue(workPattern?.monday),
      tuesday: getDefaultValue(workPattern?.tuesday),
      wednesday: getDefaultValue(workPattern?.wednesday),
      thursday: getDefaultValue(workPattern?.thursday),
      friday: getDefaultValue(workPattern?.friday),
      saturday: getDefaultValue(workPattern?.saturday),
      sunday: getDefaultValue(workPattern?.sunday),
    },
  });

  const workPatternSave = useWorkPatternSave(control);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await workPatternSave.mutateAsync(
        {
          id: workPattern?.id,
          payload: {
            title: data.title,
            monday: data.monday || "0.00",
            tuesday: data.tuesday || "0.00",
            wednesday: data.wednesday || "0.00",
            thursday: data.thursday || "0.00",
            friday: data.friday || "0.00",
            saturday: data.saturday || "0.00",
            sunday: data.sunday || "0.00",
          } as ApiWorkPatternPayload,
        },
        {
          onSuccess: (data) => {
            if (workPattern) {
              analyticsService.trackEvent(analyticEvents.probationPolicies.edited, {
                [analyticProperties.id]: workPattern.id,
              });
            } else {
              analyticsService.trackEvent(analyticEvents.probationPolicies.created, {
                [analyticProperties.id]: data.id,
              });
            }

            close(id);
          },
        }
      );
    } catch {}
  });

  return (
    <Modal
      {...props}
      title={t("core:work_pattern")}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            type={"submit"}
            size={"lg"}
            isLoading={workPatternSave.isPending}
            onClick={onSubmit}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Input
          label={t("common:title")}
          required={true}
          type={"text"}
          autoFocus={true}
          {...register("title")}
          error={errors.title?.message}
        />
        <Input
          label={t("common:monday")}
          type={"number"}
          {...register("monday")}
          error={errors.monday?.message}
        />
        <Input
          label={t("common:tuesday")}
          type={"number"}
          {...register("tuesday")}
          error={errors.tuesday?.message}
        />
        <Input
          label={t("common:wednesday")}
          type={"number"}
          {...register("wednesday")}
          error={errors.wednesday?.message}
        />
        <Input
          label={t("common:thursday")}
          type={"number"}
          {...register("thursday")}
          error={errors.thursday?.message}
        />
        <Input
          label={t("common:friday")}
          type={"number"}
          {...register("friday")}
          error={errors.friday?.message}
        />
        <Input
          label={t("common:saturday")}
          type={"number"}
          {...register("saturday")}
          error={errors.saturday?.message}
        />
        <Input
          label={t("common:sunday")}
          type={"number"}
          {...register("sunday")}
          error={errors.sunday?.message}
        />
      </form>
    </Modal>
  );
}
